import Button from "@/components/Button";
import Banner from "@/components/Banner";
import Checkbox from "@/components/Checkbox";
import TextField from "@/components/TextField";
import pluralize from "@/lib/pluralize";
import { useMutation, useQuery } from "@apollo/client";
import { useForm, FormProvider, Controller } from "react-hook-form";
import { zodV3Resolver as zodResolver } from "@/lib/zodV3Resolver";
import { z } from "zod/v3";
import { useEffect, useRef, useState } from "react";
import { FACILITY_AFFILIATION_CHANGE_IMPACT_QUERY, PATCH_FACILITY_MUTATION } from "@/features/facility/queries";
import { EditProps } from "./types";
import { useFlags } from "@/hooks/useFeatureFlags";
import SectionAutocomplete from "@/features/facility/components/ContactDetails/forms/inputs/SectionAutocomplete";
import ChapterAutocomplete from "@/features/facility/components/ContactDetails/forms/inputs/ChapterAutocomplete";
import { NullableStringSchema } from "./validation";
import useCapabilities, {
  DOMAIN,
  PERMISSION,
  useCanManageChapters,
  useHasCapabilityBool
} from "@/hooks/useCapabilities";

const SectionAffiliationSchema = z
  .object({
    sectionId: NullableStringSchema,
    chapterId: NullableStringSchema,
    propagateChapterAffiliations: z.boolean().default(false),
    chapterPropagationNote: z.string().optional()
  })
  .superRefine((values, ctx) => {
    if (values.propagateChapterAffiliations && !values.chapterPropagationNote?.trim()) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["chapterPropagationNote"],
        message: "Add a note before updating active on-site people."
      });
    }
  });

type FieldValues = z.infer<typeof SectionAffiliationSchema>;

export type SectionAffiliationPatchInput = {
  id: string;
  sectionId?: string | null;
  chapterId?: string | null;
  propagateChapterAffiliations?: boolean;
  chapterPropagationNote?: string;
};

export function buildSectionAffiliationPatchInput({
  facilityId,
  values,
  initialSectionId,
  initialChapterId
}: {
  facilityId: string;
  values: FieldValues;
  initialSectionId?: string | null;
  initialChapterId?: string | null;
}): SectionAffiliationPatchInput {
  const input: SectionAffiliationPatchInput = { id: facilityId };

  if (values.sectionId !== (initialSectionId || null)) {
    input.sectionId = values.sectionId;
  }

  if (values.chapterId !== (initialChapterId || null)) {
    input.chapterId = values.chapterId;
  }

  if (values.propagateChapterAffiliations) {
    input.propagateChapterAffiliations = true;
    input.chapterPropagationNote = values.chapterPropagationNote?.trim();
  }

  return input;
}

interface SectionAffiliationEditFormProps extends EditProps {
  facilityName: string;
  section?: { id: string; name: string } | null;
  chapter?: { id: string; name: string } | null;
}

const defaultErrorMessage = "Failed to update section affiliation";

export default function SectionAffiliationEditForm({
  facilityId,
  facilityName,
  section: initialSection,
  chapter: initialChapter,
  onCancel,
  onSuccess,
  onError
}: SectionAffiliationEditFormProps) {
  const { hasPermission } = useCapabilities();
  const flags = useFlags();
  const canEditFacilitySection = hasPermission(PERMISSION.editFacilitySection);
  const impactPreviewEnabled = !!flags["backoffice-facility-section-change"];
  const canManageChapters = useCanManageChapters();
  const canPreviewAffiliationImpact = useHasCapabilityBool({
    domain: DOMAIN.PEOPLE,
    permission: PERMISSION.viewPeople
  });
  const form = useForm<FieldValues>({
    defaultValues: {
      sectionId: initialSection?.id || "",
      chapterId: initialChapter?.id || "",
      propagateChapterAffiliations: false,
      chapterPropagationNote: ""
    },
    resolver: zodResolver(SectionAffiliationSchema),
    mode: "onChange"
  });
  const selectedSectionId = form.watch("sectionId");
  const selectedChapterId = form.watch("chapterId");
  const [propagateChapterAffiliations, setPropagateChapterAffiliations] = useState(false);
  const previousSectionIdRef = useRef(selectedSectionId);
  const hasAffiliationChange =
    selectedSectionId !== (initialSection?.id || "") || selectedChapterId !== (initialChapter?.id || "");
  const shouldPreviewImpact =
    impactPreviewEnabled && hasAffiliationChange && !!selectedSectionId && canPreviewAffiliationImpact;

  const [patchFacility, { loading }] = useMutation(PATCH_FACILITY_MUTATION);
  const {
    data: impactData,
    loading: impactLoading,
    error: impactError
  } = useQuery(FACILITY_AFFILIATION_CHANGE_IMPACT_QUERY, {
    variables: {
      facilityId,
      sectionId: selectedSectionId || null,
      chapterId: selectedChapterId || null,
      peopleLimit: 5
    },
    skip: !shouldPreviewImpact
  });
  const impact = impactData?.facilityAffiliationChangeImpact;
  const canPropagateChapterChange =
    shouldPreviewImpact &&
    canManageChapters &&
    !!impact &&
    impact.chapterChange &&
    !impact.sectionChange &&
    impact.affectedOnSitePeopleCount > 0 &&
    !impactLoading &&
    !impactError;

  useEffect(() => {
    if (previousSectionIdRef.current !== selectedSectionId) {
      form.setValue("chapterId", "");
      form.setValue("propagateChapterAffiliations", false);
      form.setValue("chapterPropagationNote", "");
      setPropagateChapterAffiliations(false);
      previousSectionIdRef.current = selectedSectionId;
    }
  }, [form, selectedSectionId]);

  useEffect(() => {
    if (!canPropagateChapterChange && propagateChapterAffiliations) {
      form.setValue("propagateChapterAffiliations", false);
      form.setValue("chapterPropagationNote", "");
      setPropagateChapterAffiliations(false);
    }
  }, [canPropagateChapterChange, form, propagateChapterAffiliations]);

  async function onSubmit(values: FieldValues) {
    try {
      const input = buildSectionAffiliationPatchInput({
        facilityId,
        values,
        initialSectionId: initialSection?.id,
        initialChapterId: initialChapter?.id
      });

      const { data } = await patchFacility({
        variables: {
          input
        }
      });

      const {
        patchFacility: { success, facility, message }
      } = data;

      if (success) {
        return onSuccess(facility);
      }
      return onError(message || defaultErrorMessage);
    } catch (error) {
      return onError((error as Error).message || defaultErrorMessage);
    }
  }

  function renderImpactBanner() {
    if (!impactPreviewEnabled) return null;
    if (!hasAffiliationChange) return null;

    if (!selectedSectionId) {
      return (
        <Banner
          inline
          variant="warn"
          testId="facility-affiliation-impact-banner"
          description="Select a section before saving this facility affiliation change."
        />
      );
    }

    if (!canPreviewAffiliationImpact) {
      return (
        <Banner
          inline
          variant="info"
          testId="facility-affiliation-impact-banner"
          description="Saving still updates only the facility."
          subText="People-impact previews are available to users with People access."
        />
      );
    }

    if (impactLoading) {
      return (
        <Banner
          inline
          variant="info"
          testId="facility-affiliation-impact-banner"
          description="Checking whether this change affects active on-site people..."
        />
      );
    }

    if (impactError || !impactData) {
      return (
        <Banner
          inline
          variant="warn"
          testId="facility-affiliation-impact-banner"
          description="Unable to preview affected people for this facility affiliation change."
          subText="Saving still updates only the facility. Review the People tab before making downstream person or membership changes."
        />
      );
    }

    const impact = impactData.facilityAffiliationChangeImpact;
    const affectedPeopleText = `${impact.affectedOnSitePeopleCount.toLocaleString()} active on-site ${pluralize(
      impact.affectedOnSitePeopleCount,
      "person",
      undefined,
      "people"
    )} may derive section or chapter data from this facility.`;
    const remoteText =
      impact.excludedRemotePeopleCount > 0
        ? `${impact.excludedRemotePeopleCount.toLocaleString()} active remote ${pluralize(
            impact.excludedRemotePeopleCount,
            "person",
            undefined,
            "people"
          )} excluded because remote employments use the person's home-address section.`
        : undefined;
    const bandText =
      impact.activeMembersRequiringBandReviewCount > 0
        ? `${impact.activeMembersRequiringBandReviewCount.toLocaleString()} affected ${pluralize(
            impact.activeMembersRequiringBandReviewCount,
            "person",
            undefined,
            "people"
          )} have active membership bands that may need section review before any person propagation.`
        : undefined;
    const chapterText =
      impact.chapterOnlyUpdatesCount > 0
        ? `${impact.chapterOnlyUpdatesCount.toLocaleString()} affected ${pluralize(
            impact.chapterOnlyUpdatesCount,
            "person",
            undefined,
            "people"
          )} may need chapter reconciliation if chapter propagation is added later.`
        : undefined;
    const previewedPeople = impact.people.map(({ person }) => person.displayName).join(", ");
    const detailLines = [
      affectedPeopleText,
      remoteText,
      bandText,
      chapterText,
      impact.noteRequired
        ? "If downstream people are updated later, add notes explaining that the change came from this facility affiliation update."
        : undefined
    ].filter((line): line is string => Boolean(line));
    const previewedPeopleText =
      previewedPeople.length > 0
        ? `${previewedPeople}${impact.affectedOnSitePeopleCount > impact.people.length ? ", and more." : "."}`
        : undefined;

    return (
      <Banner
        inline
        variant={impact.requiresManualReview ? "warn" : "info"}
        testId="facility-affiliation-impact-banner"
        description={
          impact.requiresManualReview
            ? "Review affected people before saving this facility section change."
            : "This facility affiliation change may affect active on-site people."
        }
        subText={
          <div className="mt-2 flex flex-col gap-2">
            <ul className="list-disc space-y-1 pl-5">
              {detailLines.map((line) => (
                <li key={line}>{line}</li>
              ))}
            </ul>
            {previewedPeopleText && (
              <p>
                <span className="font-semibold">Previewed people:</span> {previewedPeopleText}
              </p>
            )}
          </div>
        }
      />
    );
  }

  function defaultPropagationNote() {
    const previousChapter = impact?.currentChapter?.name || "None";
    const proposedChapter = impact?.proposedChapter?.name || "None";

    return `Updated because facility ${facilityName} changed chapter from ${previousChapter} to ${proposedChapter}. Active on-site people were updated from this facility chapter change.`;
  }

  function renderChapterPropagationFields() {
    if (!canPropagateChapterChange) return null;

    return (
      <div className="flex flex-col gap-4 rounded border border-sys-brand-outline-variant p-4">
        <Checkbox
          name="propagate-chapter-affiliations"
          checked={propagateChapterAffiliations}
          onChange={(checked) => {
            setPropagateChapterAffiliations(checked);
            form.setValue("propagateChapterAffiliations", checked, { shouldDirty: true, shouldValidate: true });
            if (checked && !form.getValues("chapterPropagationNote")?.trim()) {
              form.setValue("chapterPropagationNote", defaultPropagationNote(), {
                shouldDirty: true,
                shouldValidate: true
              });
            }
            if (!checked) {
              form.setValue("chapterPropagationNote", "", { shouldDirty: true, shouldValidate: true });
            }
          }}
          title="Update active on-site people"
          label="Reconcile chapter affiliations for active on-site people and add this note to the facility and affected people."
        />
        {propagateChapterAffiliations && (
          <Controller
            name="chapterPropagationNote"
            control={form.control}
            render={({ field, fieldState }) => (
              <TextField
                label="Propagation note"
                type="textarea"
                rows={4}
                required
                value={field.value || ""}
                onChangeText={field.onChange}
                error={!!fieldState.error}
                errorText={fieldState.error?.message}
              />
            )}
          />
        )}
      </div>
    );
  }

  return (
    <FormProvider {...form}>
      <div className="flex flex-col gap-6" data-testid="section-affiliation-edit-form">
        <SectionAutocomplete
          name="sectionId"
          label="Section Name"
          initialQuery={initialSection?.name || ""}
          disabled={!canEditFacilitySection}
          supportingText={
            !canEditFacilitySection ? "Contact Membership Services to request a section affiliation change." : ""
          }
        />
        <ChapterAutocomplete
          name="chapterId"
          label="Section Chapter Name"
          sectionId={selectedSectionId || undefined}
          initialChapter={initialChapter}
        />
        {renderImpactBanner()}
        {renderChapterPropagationFields()}
        <div className="flex flex-wrap justify-end gap-1 items-center">
          <Button variant="text" onClick={onCancel}>
            Cancel
          </Button>
          <Button disabled={loading || (shouldPreviewImpact && impactLoading)} onClick={form.handleSubmit(onSubmit)}>
            Save
          </Button>
        </div>
      </div>
    </FormProvider>
  );
}
